/**
 * UnitQuantityInput Component
 * Input component for unit-based selling (kg, yard, meter, etc.)
 * Supports decimal quantities with step increments
 */

import React, { useState, useEffect } from 'react';
import './UnitQuantityInput.css';

// Unit information with short names and decimal support
const UNITS = {
  PIECE: { name: 'Piece', shortName: 'pc', allowDecimal: false },
  KG: { name: 'Kilogram', shortName: 'kg', allowDecimal: true },
  G: { name: 'Gram', shortName: 'g', allowDecimal: true },
  LB: { name: 'Pound', shortName: 'lb', allowDecimal: true },
  OZ: { name: 'Ounce', shortName: 'oz', allowDecimal: true },
  METER: { name: 'Meter', shortName: 'm', allowDecimal: true },
  YARD: { name: 'Yard', shortName: 'yd', allowDecimal: true },
  FT: { name: 'Foot', shortName: 'ft', allowDecimal: true },
  CM: { name: 'Centimeter', shortName: 'cm', allowDecimal: true },
  INCH: { name: 'Inch', shortName: 'in', allowDecimal: true },
  LITER: { name: 'Liter', shortName: 'L', allowDecimal: true },
  ML: { name: 'Milliliter', shortName: 'ml', allowDecimal: true },
  GALLON: { name: 'Gallon', shortName: 'gal', allowDecimal: true },
  SQM: { name: 'Square Meter', shortName: 'sqm', allowDecimal: true },
  SQFT: { name: 'Square Foot', shortName: 'sqft', allowDecimal: true },
};

function UnitQuantityInput({
  unitOfMeasure = 'KG',
  pricePerUnit,
  quantity,
  onQuantityChange,
  minQuantity = 0.1,
  stepIncrement = 0.1,
  maxQuantity,
  formatPrice,
  disabled = false,
  primaryColor = '#0d7463',
}) {
  const [inputValue, setInputValue] = useState(quantity.toString());

  const unitInfo = UNITS[unitOfMeasure] || { name: unitOfMeasure, shortName: unitOfMeasure.toLowerCase(), allowDecimal: true };
  const allowDecimal = unitInfo.allowDecimal;

  // Update input value when quantity prop changes
  useEffect(() => {
    setInputValue(quantity.toString());
  }, [quantity]);

  // Format quantity to appropriate decimal places
  const formatQuantity = (value) => {
    if (!allowDecimal) {
      return Math.round(value);
    }
    // Round to 3 decimal places
    return Math.round(value * 1000) / 1000;
  };

  // Handle increment
  const handleIncrement = () => {
    if (disabled) return;
    const step = stepIncrement || (allowDecimal ? 0.1 : 1);
    let newValue = quantity + step;
    if (maxQuantity !== undefined && newValue > maxQuantity) {
      newValue = maxQuantity;
    }
    const formatted = formatQuantity(newValue);
    onQuantityChange(formatted);
  };

  // Handle decrement
  const handleDecrement = () => {
    if (disabled) return;
    const step = stepIncrement || (allowDecimal ? 0.1 : 1);
    let newValue = quantity - step;
    const min = minQuantity || (allowDecimal ? 0.1 : 1);
    if (newValue < min) {
      newValue = min;
    }
    const formatted = formatQuantity(newValue);
    onQuantityChange(formatted);
  };

  // Handle direct input
  const handleInputChange = (e) => {
    const text = e.target.value;

    // Allow empty string for editing
    if (text === '' || text === '.') {
      setInputValue(text);
      return;
    }

    // Validate input based on unit type
    const regex = allowDecimal ? /^\d*\.?\d*$/ : /^\d*$/;
    if (!regex.test(text)) {
      return;
    }

    setInputValue(text);
  };

  // Handle input blur - validate and update quantity
  const handleInputBlur = () => {
    let value = parseFloat(inputValue);

    if (isNaN(value) || value <= 0) {
      value = minQuantity || (allowDecimal ? 0.1 : 1);
    }

    if (maxQuantity !== undefined && value > maxQuantity) {
      value = maxQuantity;
    }

    const min = minQuantity || (allowDecimal ? 0.1 : 1);
    if (value < min) {
      value = min;
    }

    const formatted = formatQuantity(value);
    setInputValue(formatted.toString());
    onQuantityChange(formatted);
  };

  // Calculate total
  const total = quantity * pricePerUnit;

  const canDecrement = quantity > (minQuantity || (allowDecimal ? 0.1 : 1));
  const canIncrement = maxQuantity === undefined || quantity < maxQuantity;

  return (
    <div className="uqi-container">
      {/* Price per unit label */}
      <div className="uqi-price-row">
        <span className="uqi-price-label">Price per {unitInfo.shortName}</span>
        <span className="uqi-price-value" style={{ color: primaryColor }}>
          {formatPrice(pricePerUnit)}/{unitInfo.shortName}
        </span>
      </div>

      {/* Quantity input */}
      <div className="uqi-input-row">
        <label className="uqi-quantity-label">Quantity ({unitInfo.shortName})</label>
        <div className="uqi-input-container">
          <button
            type="button"
            onClick={handleDecrement}
            disabled={disabled || !canDecrement}
            className={`uqi-step-btn ${(!canDecrement || disabled) ? 'uqi-step-btn--disabled' : ''}`}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M5 12h14" />
            </svg>
          </button>

          <input
            type="text"
            className="uqi-input"
            value={inputValue}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            disabled={disabled}
          />

          <button
            type="button"
            onClick={handleIncrement}
            disabled={disabled || !canIncrement}
            className={`uqi-step-btn ${(!canIncrement || disabled) ? 'uqi-step-btn--disabled' : ''}`}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 5v14M5 12h14" />
            </svg>
          </button>
        </div>
      </div>

      {/* Total calculation */}
      <div className="uqi-total-row">
        <span className="uqi-total-label">
          Total ({quantity} {unitInfo.shortName} x {formatPrice(pricePerUnit)})
        </span>
        <span className="uqi-total-value">
          {formatPrice(total)}
        </span>
      </div>
    </div>
  );
}

export default UnitQuantityInput;
